import React, { useState, useCallback } from 'react';
import useOrganization from '../../../../../../../hooks/api/useOrganization';
import MainContentContainer from '../../../../../../v2BaseComponents/MainContentContainer';
import Stack from '../../../../../../v2BaseComponents/Stack';
import Text from '../../../../../../v2BaseComponents/Text';
import { Input } from '../../../../../../v2BaseComponents/Inputs';
import { ActionButtonLayout } from '../../../../../../v2BaseComponents/ActionButtonLayout';
import { getIntl } from '../../../../../../../utils/intl';

const VALID_NUMBERS_REGEX = /^\d+$/;

const intl = getIntl('AuthenticationPolicesDetailsContent', {
    save: 'Save',
    cancel: 'Cancel',
    sessionTimeoutMin: 'Session Timeout (Minutes)',
    sessionTimeoutPlaceholder: 'No Timeout Set',
});

export type OrganizationEditContentOwnProps = {
    orgSessionTimeout: number | null | undefined;
    onCancel: () => void;
    OnUpdate: (updatedSessionTimeoutMin: number) => void;
};

export type OrganizationEditContentProps = OrganizationEditContentOwnProps;

const SecuritySettingsEditContent: React.FC<OrganizationEditContentProps> = (props) => {
    const [sessionTimeoutMin, setSessionTimeoutMin] = useState<string>(props.orgSessionTimeout?.toString() || '');

    const { isUpdatingOrganization, updateOrganization } = useOrganization({
        onOrganizationUpdate: async (updatedOrg) => {
            // Small delay to ensure state updates are processed
            await new Promise((resolve) => setTimeout(resolve, 900));
            props.OnUpdate(updatedOrg?.metadata.sessionTimeoutMinutes || 0);
        },
    });

    const handleSessionTimeoutChange = useCallback((e: any) => {
        const value = e.target.value;
        // Allow empty string or valid numbers only
        if (value === '' || VALID_NUMBERS_REGEX.test(value)) {
            setSessionTimeoutMin(value);
        }
    }, []);

    const handleSave = useCallback(() => {
        const sessionTimeoutMinutes = sessionTimeoutMin === '' ? null : parseInt(sessionTimeoutMin, 10);
        if (props.orgSessionTimeout != sessionTimeoutMinutes) {
            updateOrganization({
                sessionTimeoutMinutes,
            });
        }
    }, [sessionTimeoutMin, props.orgSessionTimeout, updateOrganization]);

    return (
        <ActionButtonLayout
            scrollable
            actions={[
                {
                    title: intl('cancel'),
                    onPress: props.onCancel,
                    icon: 'XCircle',
                    testID: 'cancel-edit-session-timeout-button',
                    variant: 'secondary',
                },
                {
                    title: intl('save'),
                    onPress: handleSave,
                    icon: 'Save',
                    testID: 'save-edit-session-timeout-button',
                    isDisabled: isUpdatingOrganization,
                },
            ]}
            whiteBackground
        >
            <MainContentContainer>
                <Stack vertical fullWidth align="flex-start" space={10} testID="security-settings-details-content">
                    <Text variant="bold-h4" testID="security-settings-form">
                        Access Controls
                    </Text>
                    <Input
                        onChange={handleSessionTimeoutChange}
                        title={sessionTimeoutMin ? intl('sessionTimeoutMin') : intl('sessionTimeoutPlaceholder')}
                        value={sessionTimeoutMin}
                        testID="sessionTimeout"
                    />
                </Stack>
            </MainContentContainer>
        </ActionButtonLayout>
    );
};

export default React.memo(SecuritySettingsEditContent);
